import { type JSX, useCallback, useEffect, useRef, useState } from 'react';

import {
  bausteinfolge as ladeFolge,
  bausteinfolgeSetzen,
  type Bausteinfolge,
} from '../api/produkte';
import { bausteine as ladeBausteine, BAUSTEINARTNAME, type Bausteinzeile } from '../api/bausteine';
import { ApiFehler } from '../api/fehler';
import { Meldung } from '../komponenten/Meldung';
import { Abschnitt } from '../komponenten/Akte';

/**
 * Die Trainingsdefinition (PRD-29, TAG-07, DAT-22).
 *
 * ============================================================================
 * ZWEI VERSCHIEDENE DINGE STEHEN IN EINER LISTE
 * ============================================================================
 *
 * Die MENGE der Bausteine ist fest — sie ist das, was verkauft wird. Ihre
 * REIHENFOLGE ist es nicht: Fällt ein Modul aus, tauscht die Tagesplanung
 * zwei Stationen. Beides zugleich geht nur mit einer Grenze, und die ist die
 * Phase.
 *
 * Deshalb zeigt diese Maske nicht einfach zehn Zeilen, sondern zehn Zeilen in
 * Phasen gruppiert — und schreibt an jede Phase mit mehr als einem Baustein
 * ausdrücklich, dass ihre Reihenfolge frei ist. Eine reine Liste sähe aus wie
 * ein Ablaufplan, und genau das ist sie nicht.
 *
 * ============================================================================
 * DIE REIHENFOLGE IST DIE POSITION, KEIN FELD
 * ============================================================================
 *
 * Kein Nummernfeld: Zwei Quellen für dieselbe Ordnung laufen beim Verschieben
 * auseinander, und die Maske müsste nach jedem Zug neu durchnummerieren. Die
 * Position in der Liste IST die Regelreihenfolge.
 *
 * ============================================================================
 * SEIT AeA-11 IST SIE DIE VORLAGE
 * ============================================================================
 *
 * „Der Ablauf eines Trainings definiert sich aus den Bausteinen und den
 * Phasen" (Auftraggeber, 08.09.2026). Was hier steht, ist deshalb nicht mehr
 * die halbe Auskunft neben einem Ablaufschema, sondern die ganze: Die Phasen
 * laufen in ihrer Reihenfolge, und innerhalb einer Phase rotieren die
 * Stationen gegeneinander. Die Tagesplanung leitet den Tag daraus ab.
 */

type Entwurfszeile = {
  readonly baustein: string;
  readonly phase: number;
  /** Als Text, damit ein leeres Feld „Regeldauer" heißen kann und nicht 0. */
  readonly dauer: string;
};

function ausFolge(folge: Bausteinfolge): Entwurfszeile[] {
  return folge.zeilen.map((z) => ({
    baustein: z.baustein,
    phase: z.phase,
    dauer: z.dauerBloecke === null ? '' : String(z.dauerBloecke),
  }));
}

/**
 * Die Phasen lückenlos ab 1 — nach jedem Zug neu.
 *
 * Der Trigger aus 0129 weist eine Lücke ab, und das zu Recht: Eine Folge mit
 * den Phasen 1, 2, 4 hat keine dritte, und niemand sähe, ob eine vergessen
 * wurde oder ob die Nummer nur ein Sprung ist. Die Maske lässt es deshalb gar
 * nicht erst entstehen, statt den Anwender in eine Abweisung laufen zu
 * lassen.
 *
 * Die REIHENFOLGE der Phasen bleibt dabei erhalten — verdichtet wird nur die
 * Nummerierung.
 */
function verdichte(zeilen: readonly Entwurfszeile[]): Entwurfszeile[] {
  const vorhanden = [...new Set(zeilen.map((z) => z.phase))].sort((a, b) => a - b);
  const neu = new Map(vorhanden.map((p, i) => [p, i + 1]));
  return zeilen.map((z) => ({ ...z, phase: neu.get(z.phase) ?? 1 }));
}

export function Trainingsdefinition({
  artikel,
  darfKonfigurieren,
  beiSchliessen,
}: {
  readonly artikel: string;
  readonly darfKonfigurieren: boolean;
  readonly beiSchliessen: () => void;
}): JSX.Element {
  const [folge, setzeFolge] = useState<Bausteinfolge | null>(null);
  const [katalog, setzeKatalog] = useState<readonly Bausteinzeile[]>([]);
  const [entwurf, setzeEntwurf] = useState<Entwurfszeile[]>([]);
  const [blockMinuten, setzeBlockMinuten] = useState<number | null>(null);
  const [laedt, setzeLaedt] = useState(true);
  const [fehler, setzeFehler] = useState<ApiFehler | null>(null);
  const [meldung, setzeMeldung] = useState<string | null>(null);
  const [neuerBaustein, setzeNeuerBaustein] = useState('');

  const holen = useCallback(async () => {
    setzeLaedt(true);
    try {
      const [f, b] = await Promise.all([ladeFolge(artikel), ladeBausteine()]);
      setzeFolge(f);
      setzeEntwurf(ausFolge(f));
      setzeKatalog(b.bausteine.filter((z) => z.aktiv));
      setzeBlockMinuten(
        typeof b.blocklaengeMinuten === 'number' ? b.blocklaengeMinuten : null,
      );
      setzeFehler(null);
    } catch (f) {
      setzeFehler(f instanceof ApiFehler ? f : null);
      if (!(f instanceof ApiFehler)) throw f;
    } finally {
      setzeLaedt(false);
    }
  }, [artikel]);

  useEffect(() => {
    void holen();
  }, [holen]);

  /*
   * Nach ↑ / ↓ bleibt der Fokus beim verschobenen Baustein: auf demselben
   * Pfeil in seiner neuen Zeile, damit man mit der Tastatur weiterschieben
   * kann. Steht er danach am Rand, ist dieser Pfeil gesperrt — dann der
   * andere.
   */
  const tabelle = useRef<HTMLTableElement>(null);
  const fokusNachZug = useRef<{ zeile: number; richtung: 'oben' | 'unten' } | null>(null);
  useEffect(() => {
    const ziel = fokusNachZug.current;
    if (ziel === null) return;
    fokusNachZug.current = null;
    const pfeile = [
      ...(tabelle.current?.tBodies[0]?.rows[ziel.zeile]?.querySelectorAll<HTMLButtonElement>(
        'button[data-richtung]',
      ) ?? []),
    ].filter((k) => !k.disabled);
    (pfeile.find((k) => k.dataset.richtung === ziel.richtung) ?? pfeile[0])?.focus();
  }, [entwurf]);

  const name = (schluessel: string): string =>
    katalog.find((b) => b.schluessel === schluessel)?.bezeichnung ?? schluessel;

  const regeldauer = (schluessel: string): number =>
    katalog.find((b) => b.schluessel === schluessel)?.regeldauerBloecke ?? 0;

  const geaendert =
    folge !== null && JSON.stringify(entwurf) !== JSON.stringify(ausFolge(folge));

  function verschiebe(i: number, um: number): void {
    const ziel = i + um;
    if (ziel < 0 || ziel >= entwurf.length) return;
    const kopie = [...entwurf];
    const [zeile] = kopie.splice(i, 1);
    kopie.splice(ziel, 0, zeile!);
    fokusNachZug.current = { zeile: ziel, richtung: um < 0 ? 'oben' : 'unten' };
    setzeEntwurf(verdichte(kopie));
  }

  async function speichern(): Promise<void> {
    setzeMeldung(null);
    try {
      const neu = await bausteinfolgeSetzen(
        artikel,
        verdichte(entwurf).map((z) => ({
          baustein: z.baustein,
          phase: z.phase,
          dauerBloecke: z.dauer.trim() === '' ? null : Number(z.dauer),
        })),
      );
      setzeFolge(neu);
      setzeEntwurf(ausFolge(neu));
      setzeMeldung(
        neu.fassung === null
          ? 'Die Trainingsdefinition ist gespeichert.'
          : `Gespeichert — das ist jetzt Fassung ${String(neu.fassung.nummer)}.`,
      );
      setzeFehler(null);
    } catch (f) {
      setzeFehler(f instanceof ApiFehler ? f : null);
      if (!(f instanceof ApiFehler)) throw f;
    }
  }

  if (laedt) return <p className="hinweiszeile">Wird geladen …</p>;
  if (folge === null) {
    return (
      <div className="tafel__abschnitte">
        {fehler !== null && <Meldung fehler={fehler} />}
        <div className="knopfreihe">
          <button type="button" className="knopf" onClick={beiSchliessen}>
            Zurück zum Produkt
          </button>
        </div>
      </div>
    );
  }

  const verdichtet = verdichte(entwurf);
  const phasen = [...new Set(verdichtet.map((z) => z.phase))];

  /*
   * ÄA-41: Die Definition steht in der Artikelakte an der Stelle der Reiter —
   * Kopf und Seitenleiste des Artikels bleiben. Deshalb eine Überschrift der
   * zweiten Stufe: Die erste ist der Artikel.
   */
  return (
    <div className="tafel__abschnitte">
      <div className="karte__titel">
        <h2>Trainingsdefinition</h2>
        <small>
          {folge.artikel.artikelnummer} · {folge.artikel.bezeichnung}
          {folge.fassung !== null && <> · Fassung {folge.fassung.nummer}</>}
        </small>
      </div>
      <div className="knopfreihe">
        <button type="button" className="knopf" onClick={beiSchliessen}>
          Zurück zum Produkt
        </button>
        {darfKonfigurieren && (
          <button
            type="button"
            className="knopf knopf--haupt"
            disabled={!geaendert}
            onClick={() => void speichern()}
          >
            Speichern
          </button>
        )}
      </div>

      {fehler !== null && <Meldung fehler={fehler} />}
      {meldung !== null && (
        <p className="hinweiszeile" role="status">
          {meldung}
        </p>
      )}

      {/*
        WAS DIESE MASKE ZUSICHERT, STEHT DARÜBER UND NICHT IN EINER HILFE.
        Wer hier etwas ändert, ändert den Gegenstand des Qualitätsmanagements
        und erzeugt eine neue Produktfassung — das gehört vor die Liste.
      */}
      {/* PRD-30 */}
      <p className="hilfe">
        Diese Bausteine enthält das Training — <strong>immer</strong>. Innerhalb einer
        Phase darf die Tagesplanung die Reihenfolge tauschen, über Phasengrenzen hinweg
        nicht. Eine Änderung hier erzeugt eine neue Produktfassung; laufende
        Termine behalten ihre.
      </p>

      {verdichtet.length === 0 ? (
        <p className="hinweiszeile">
          Für dieses Produkt ist noch keine Trainingsdefinition hinterlegt. Ohne sie gibt
          es keine Leistungsmerkmale für den Vertrieb — und keinen Ablauf, den die
          Tagesplanung anordnen könnte.
        </p>
      ) : (
        <div className="tabellenhuelle tabellenhuelle--telefon">
          <table className="liste__tabelle" ref={tabelle}>
            <thead>
              <tr>
                <th scope="col">Nr.</th>
                <th scope="col">Baustein</th>
                <th scope="col">Art</th>
                <th scope="col">Dauer</th>
                <th scope="col">Phase</th>
                {/* Die Knöpfe sprechen für sich; der Kopf ist für Vorleseprogramme. */}
                {darfKonfigurieren && (
                  <th scope="col" className="zeilenhandlungen__spalte">
                    <span className="nur-vorlesen">Handlungen</span>
                  </th>
                )}
              </tr>
            </thead>
            <tbody>
              {verdichtet.map((z, i) => {
                const ersterDerPhase = i === 0 || verdichtet[i - 1]!.phase !== z.phase;
                const inPhase = verdichtet.filter((x) => x.phase === z.phase).length;
                return (
                  <tr key={`${String(i)}-${z.baustein}`}>
                    <td>{i + 1}</td>
                    <td>
                      {name(z.baustein)} <code className="hilfe">{z.baustein}</code>
                      {/*
                        Die Aussage der Phase steht EINMAL je Phase und nicht an
                        jeder Zeile — sonst liest man sie nach dem dritten Mal
                        nicht mehr.
                      */}
                      {ersterDerPhase && inPhase > 1 && (
                        <p className="hilfe">
                          Phase {z.phase}: diese {inPhase} Bausteine sind untereinander frei
                          vertauschbar.
                        </p>
                      )}
                    </td>
                    <td>{artName(katalog, z.baustein)}</td>
                    <td>
                      {darfKonfigurieren ? (
                        <input
                          type="number"
                          min={1}
                          max={32}
                          className="feld__eingabe feld__eingabe--schmal"
                          aria-label={`Dauer in Blöcken, Zeile ${String(i + 1)}`}
                          placeholder={String(regeldauer(z.baustein))}
                          value={z.dauer}
                          onChange={(e) => {
                            const kopie = [...entwurf];
                            kopie[i] = { ...kopie[i]!, dauer: e.target.value };
                            setzeEntwurf(kopie);
                          }}
                        />
                      ) : (
                        <>{z.dauer === '' ? regeldauer(z.baustein) : z.dauer}</>
                      )}{' '}
                      <span className="hilfe">
                        {z.dauer === '' ? 'Regeldauer' : 'abweichend'}
                        {blockMinuten !== null &&
                          ` · ${String(
                            (z.dauer === '' ? regeldauer(z.baustein) : Number(z.dauer)) *
                              blockMinuten,
                          )} min`}
                      </span>
                    </td>
                    <td>{z.phase}</td>
                    {darfKonfigurieren && (
                      <td className="zeilenhandlungen__spalte">
                        <div className="zeilenhandlungen">
                          <div
                            className="zeilenhandlungen__gruppe zeilenhandlungen__segment"
                            role="group"
                            aria-label={`Verschieben, Zeile ${String(i + 1)}`}
                          >
                            <button
                              type="button"
                              className="knopf knopf--klein"
                              aria-label={`Zeile ${String(i + 1)} nach oben`}
                              title={i === 0 ? 'Steht schon oben' : 'Eine Zeile nach oben'}
                              data-richtung="oben"
                              disabled={i === 0}
                              onClick={() => {
                                verschiebe(i, -1);
                              }}
                            >
                              ↑
                            </button>
                            <button
                              type="button"
                              className="knopf knopf--klein"
                              aria-label={`Zeile ${String(i + 1)} nach unten`}
                              title={
                                i === verdichtet.length - 1 ? 'Steht schon unten' : 'Eine Zeile nach unten'
                              }
                              data-richtung="unten"
                              disabled={i === verdichtet.length - 1}
                              onClick={() => {
                                verschiebe(i, 1);
                              }}
                            >
                              ↓
                            </button>
                          </div>
                          {/*
                            Beide Phasenknöpfe stehen in jeder Zeile, der nicht
                            passende gesperrt — nur den passenden zu zeigen, ließe
                            die Gruppe von Zeile zu Zeile die Breite wechseln.
                            Trennen ist gesperrt, wo der Baustein schon allein in
                            seiner Phase steht: Dort änderte es nichts.
                          */}
                          <div
                            className="zeilenhandlungen__gruppe"
                            role="group"
                            aria-label={`Phase, Zeile ${String(i + 1)}`}
                          >
                            <button
                              type="button"
                              className="knopf knopf--klein"
                              aria-label={`Zeile ${String(i + 1)} in eine eigene Phase`}
                              title={
                                inPhase === 1
                                  ? 'Steht schon allein in seiner Phase'
                                  : 'In eine eigene Phase — die Position wird verbindlich'
                              }
                              disabled={inPhase === 1}
                              onClick={() => {
                                /*
                                 * Die Zeile bekommt eine Phase für sich: Ihre
                                 * Position wird damit verbindlich. `verdichte`
                                 * räumt die Nummern danach auf.
                                 */
                                const kopie = [...entwurf];
                                kopie[i] = { ...kopie[i]!, phase: kopie[i]!.phase * 100 + 1 };
                                setzeEntwurf(verdichte(kopie));
                              }}
                            >
                              Phase trennen
                            </button>
                            <button
                              type="button"
                              className="knopf knopf--klein"
                              aria-label={`Zeile ${String(i + 1)} mit der vorigen Phase verbinden`}
                              title={
                                i === 0
                                  ? 'Die erste Zeile hat keine vorige Phase'
                                  : verdichtet[i - 1]!.phase === z.phase
                                    ? 'Gehört schon zur Phase der vorigen Zeile'
                                    : 'Mit der Phase der vorigen Zeile verbinden'
                              }
                              disabled={i === 0 || verdichtet[i - 1]!.phase === z.phase}
                              onClick={() => {
                                const kopie = [...entwurf];
                                kopie[i] = { ...kopie[i]!, phase: verdichtet[i - 1]!.phase };
                                setzeEntwurf(verdichte(kopie));
                              }}
                            >
                              Phase verbinden
                            </button>
                          </div>
                          <div className="zeilenhandlungen__gruppe zeilenhandlungen__gruppe--abgesetzt">
                            <button
                              type="button"
                              className="knopf knopf--klein knopf--eingriff"
                              aria-label={`Zeile ${String(i + 1)} entfernen`}
                              title="Baustein aus der Trainingsdefinition entfernen"
                              onClick={() => {
                                setzeEntwurf(verdichte(entwurf.filter((_, k) => k !== i)));
                              }}
                            >
                              Entfernen
                            </button>
                          </div>
                        </div>
                      </td>
                    )}
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      )}

      {darfKonfigurieren && (
        <div className="feld">
          <label className="feld__beschriftung" htmlFor="tdNeu">
            Baustein hinzufügen
          </label>
          <select
            id="tdNeu"
            className="feld__eingabe"
            value={neuerBaustein}
            onChange={(e) => {
              setzeNeuerBaustein(e.target.value);
            }}
          >
            <option value="">— wählen —</option>
            {katalog.map((b) => (
              <option key={b.schluessel} value={b.schluessel}>
                {b.bezeichnung} ({BAUSTEINARTNAME[b.art]})
              </option>
            ))}
          </select>{' '}
          <button
            type="button"
            className="knopf"
            disabled={neuerBaustein === ''}
            onClick={() => {
              /*
               * Angehängt in einer EIGENEN Phase. Ihn in die letzte zu legen
               * hieße zu behaupten, er sei mit deren Bausteinen vertauschbar
               * — das weiß nur, wer das Training kennt.
               */
              setzeEntwurf(
                verdichte([
                  ...entwurf,
                  { baustein: neuerBaustein, phase: (phasen.at(-1) ?? 0) + 1, dauer: '' },
                ]),
              );
              setzeNeuerBaustein('');
            }}
          >
            Anhängen
          </button>
        </div>
      )}

      {/*
        PRD-39: Was der Vertrieb daraus benennen darf. Aus dem GESPEICHERTEN
        Stand, nicht aus dem Entwurf — sonst verspräche die Vorschau etwas,
        das noch niemand beschlossen hat.
      */}
      {/* PRD-39 */}
      <Abschnitt titel="Was der Vertrieb daraus benennt">
        {folge.leistungsmerkmale.length === 0 ? (
          <p className="hilfe">Noch nichts — es sind keine Bausteine im Leistungsversprechen.</p>
        ) : (
          <ul className="merkmalsliste">
            {folge.leistungsmerkmale.map((m) => (
              <li key={m}>{m}</li>
            ))}
          </ul>
        )}
        <p className="feld__hilfe">
          Abgeleitet aus den Bausteinen oben — Begrüßung, Pause und Abschluss stehen nicht
          darin. Der Stand nach dem letzten Speichern.
        </p>
      </Abschnitt>
    </div>
  );
}

/** Die Art des Bausteins aus dem Katalog — der Schlüssel allein sagt sie nicht. */
function artName(katalog: readonly Bausteinzeile[], schluessel: string): string {
  const art = katalog.find((b) => b.schluessel === schluessel)?.art;
  return art === undefined ? '—' : BAUSTEINARTNAME[art];
}
